import React, { useEffect, useState, useRef, useCallback } from "react";
import logo from "assets/img/logo.png";
import heroBackground from "assets/img/mufyn-phone-on-table.jpg";
import imgDashboard from "assets/img/landing/dashboard.png";
// imgAiAdvisor removed — s2 now uses code-based fake report preview
// imgCategories removed — s3 now uses code-based profile cards
import imgMobile from "assets/img/landing/mobile.png";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "config";
import packageJson from "../../../package.json";
import axios from "axios";

import {
  Box,
  Flex,
  Heading,
  Text,
  Link,
  keyframes,
  Icon,
  VStack,
  HStack,
  Container,
  useBreakpointValue,
} from "@chakra-ui/react";
import MufynButton from "components/Button";
import {
  FaArrowRight,
  FaArrowDown,
  FaGooglePlay,
  FaDesktop,
  FaTabletAlt,
  FaMobileAlt,
  FaUser,
  FaBriefcase,
  FaHeart,
  FaExchangeAlt,
} from "react-icons/fa";
import {
  IoSparkles,
  IoCheckmarkCircle,
} from "react-icons/io5";
import { FaRobot, FaFilePdf } from "react-icons/fa";

// ============================================
// ANIMATIONS
// ============================================

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(-10px) rotate(-2deg); }
  50% { transform: translateY(10px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const slideInLeft = keyframes`
  0% { opacity: 0; transform: translateX(-20px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  0% { opacity: 0; transform: translateX(20px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const heroReveal = keyframes`
  0% { opacity: 0; transform: translateY(30px) scale(0.97); filter: blur(4px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

const bounceArrow = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.6; }
  50% { transform: translateY(10px); opacity: 1; }
`;

const ripple = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
  20% { opacity: 0.4; }
  100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
`;

const heroImageDrift = keyframes`
  0% { transform: scale(1.05) translate(0, 0); }
  33% { transform: scale(1.08) translate(-0.5%, -0.3%); }
  66% { transform: scale(1.06) translate(0.4%, 0.2%); }
  100% { transform: scale(1.05) translate(0, 0); }
`;

const heroImageReveal = keyframes`
  0% { opacity: 0; }
  100% { opacity: 0.5; }
`;

const heroPopIn = keyframes`
  0% { opacity: 0; transform: scale(0.4); filter: blur(6px); }
  55% { opacity: 1; transform: scale(1.08); filter: blur(0); }
  75% { transform: scale(0.97); }
  100% { opacity: 1; transform: scale(1); }
`;

const heroRiseUp = keyframes`
  0% { opacity: 0; transform: translateY(40px); }
  60% { opacity: 1; transform: translateY(-6px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const fadeSlideLeft = keyframes`
  0% { opacity: 0; transform: translateX(-40px); filter: blur(3px); }
  100% { opacity: 1; transform: translateX(0); filter: blur(0); }
`;

const fadeSlideRight = keyframes`
  0% { opacity: 0; transform: translateX(40px); filter: blur(3px); }
  100% { opacity: 1; transform: translateX(0); filter: blur(0); }
`;

const numberCount = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const magicGlow = keyframes`
  0%, 100% { opacity: 0.25; transform: scale(1); filter: blur(40px); }
  33% { opacity: 0.45; transform: scale(1.06); filter: blur(45px); }
  66% { opacity: 0.3; transform: scale(0.97); filter: blur(38px); }
`;

const magicGlow2 = keyframes`
  0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
  50% { opacity: 0.35; transform: translate(-50%, -50%) scale(1.1) rotate(15deg); }
`;

const magicShimmer = keyframes`
  0% { opacity: 0; transform: translateX(-100%) rotate(-15deg); }
  50% { opacity: 0.08; }
  100% { opacity: 0; transform: translateX(100%) rotate(-15deg); }
`;

const gridPulse = keyframes`
  0%, 100% { opacity: 0.03; }
  50% { opacity: 0.08; }
`;

const barGrow = keyframes`
  0% { transform: scaleX(0); opacity: 0; }
  100% { transform: scaleX(1); opacity: 1; }
`;

const dataShimmer = keyframes`
  0% { background-position: -300% 0; }
  100% { background-position: 300% 0; }
`;

const deviceFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-8px) rotate(0.5deg); }
`;

const screenGlare = keyframes`
  0% { transform: translateX(-120%) rotate(-35deg); opacity: 0; }
  40% { opacity: 0.12; }
  100% { transform: translateX(120%) rotate(-35deg); opacity: 0; }
`;

// Contextual profile card hover animations
const cardBorderPulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.8; }
`;

const particlePulse = keyframes`
  0%, 100% { transform: scale(0.85); }
  50% { transform: scale(1.1); }
`;

const amountGlow = keyframes`
  0%, 100% { text-shadow: 0 0 0px transparent; }
  50% { text-shadow: 0 0 12px currentColor; }
`;

const chipShine = keyframes`
  0% { left: -40%; }
  100% { left: 120%; }
`;

const badgePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0px transparent; }
  50% { box-shadow: 0 0 10px currentColor; }
`;

// ============================================
// INTERSECTION OBSERVER HOOK
// ============================================

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15, ...options }
    );

    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return [ref, isInView];
};

// ============================================
// SCROLL SECTION — alternating left/right
// ============================================

const ScrollSection = ({ align, labelKey, titleKey, descKey, accentColor = "#22c55e", image, delay = 0, variant }) => {
  const { t } = useTranslation();
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const isLeft = align === "left";
  const isMagic = variant === "magic";
  const isDashboard = variant === "dashboard";
  const isCategories = variant === "categories";
  const isProfiles = variant === "profiles";
  const isMobile = variant === "mobile";

  // 3D tilt interaction for image sections (desktop only)
  const [tilt, setTilt] = useState({ x: 0, y: 0, mx: 50, my: 50, active: false });
  const handleMouseMove = useCallback((e) => {
    if (isCategories || isProfiles) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const strength = isMagic ? 6 : isDashboard ? 4 : isMobile ? 3 : 3;
    setTilt({
      x: (py - 0.5) * -strength,
      y: (px - 0.5) * strength,
      mx: px * 100,
      my: py * 100,
      active: true,
    });
  }, [isMagic, isDashboard, isMobile, isCategories, isProfiles]);
  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0, mx: 50, my: 50, active: false });
  }, []);

  // Profile cards: contextual hover
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Flex
      ref={ref}
      direction={{ base: "column", lg: isLeft ? "row" : "row-reverse" }}
      align="center"
      justify="space-between"
      gap={{ base: 10, lg: 20 }}
      py={{ base: 16, md: 28 }}
      opacity={isInView ? 1 : 0}
    >
      {/* Text side */}
      <Box
        flex="1"
        maxW={{ base: "100%", lg: "480px" }}
        animation={isInView ? `${isLeft ? fadeSlideLeft : fadeSlideRight} 1.3s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s both` : "none"}
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="3px"
          color={accentColor}
          mb={4}
        >
          {t(labelKey)}
        </Text>
        <Heading
          fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
          fontWeight="extrabold"
          color="white"
          lineHeight="1.1"
          mb={5}
        >
          {t(titleKey)}
        </Heading>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          color="gray.400"
          lineHeight="1.8"
          maxW="420px"
        >
          {t(descKey)}
        </Text>
      </Box>

      {/* Visual side — screenshot or custom visual */}
      <Box
        flex={isDashboard ? "1.4" : "1"}
        w={{ base: "100%", lg: "auto" }}
        maxW={{ base: "100%", lg: isDashboard ? "640px" : isMobile ? "340px" : isProfiles ? "520px" : "520px" }}
        borderRadius={isDashboard ? "24px" : "32px"}
        position="relative"
        overflow={(isMagic || isMobile || isProfiles || isDashboard) ? "visible" : "hidden"}
        animation={isInView ? `${isLeft ? fadeSlideRight : fadeSlideLeft} 1.3s cubic-bezier(0.16, 1, 0.3, 1) ${delay + 0.2}s both` : "none"}
      >
        {/* Mobile device floating effect */}
        {isMobile && (
          <>
            <Box
              position="absolute" top="-15px" left="-15px" right="-15px" bottom="-15px"
              borderRadius="40px"
              bg="radial-gradient(ellipse at 50% 80%, rgba(6, 182, 212, 0.18) 0%, transparent 60%)"
              filter="blur(25px)"
              animation={`${deviceFloat} 5s ease-in-out infinite`}
              pointerEvents="none"
              zIndex={-1}
            />
          </>
        )}

        {/* Dashboard: subtle green ambient glow */}
        {isDashboard && (
          <Box
            position="absolute" top="-15px" left="-15px" right="-15px" bottom="-15px"
            borderRadius="32px"
            bg="radial-gradient(ellipse at 40% 30%, rgba(34, 197, 94, 0.08) 0%, transparent 60%)"
            filter="blur(25px)"
            animation={`${magicGlow} 8s ease-in-out infinite 1s`}
            pointerEvents="none"
            zIndex={-1}
          />
        )}

        {/* Magic glow effects for AI advisor */}
        {isMagic && (
          <>
            {/* Outer purple glow */}
            <Box
              position="absolute" top="-20px" left="-20px" right="-20px" bottom="-20px"
              borderRadius="40px"
              bg={`radial-gradient(ellipse at 30% 20%, rgba(139, 92, 246, 0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(99, 102, 241, 0.12) 0%, transparent 55%)`}
              filter="blur(30px)"
              animation={`${magicGlow} 6s ease-in-out infinite`}
              pointerEvents="none"
              zIndex={-1}
            />
            {/* Secondary blue glow */}
            <Box
              position="absolute" top="50%" left="50%"
              w="80%" h="80%" borderRadius="full"
              bg="radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 40%, transparent 70%)"
              animation={`${magicGlow2} 8s ease-in-out infinite 1s`}
              pointerEvents="none"
              zIndex={-1}
            />
          </>
        )}

        {/* Profiles: animated profile cards with contextual hover effects */}
        {isProfiles ? (
          <Box
            position="relative"
            w="100%"
            maxW={{ base: "100%", md: "420px" }}
            mx="auto"
            h={{ base: "320px", md: "380px" }}
          >
            {[{
              typeBadge: t("landing.profiles_cards.business_badge"),
              fullLabel: t("landing.profiles_cards.business_label"),
              color: "#3b82f6",
              amount: "65,842.70 €",
              transactions: 132,
              categories: 18,
              rotation: { base: "rotate(-5deg)", md: "rotate(-8deg)" },
              top: { base: "0%", md: "-5%" },
              left: { base: "2%", md: "-8%" },
              zIndex: 1,
              gradient: "linear-gradient(135deg, #0c1929 0%, #0f2744 30%, #132d4f 60%, #0a1e38 100%)",
              glowColor: "rgba(59, 130, 246, 0.15)",
              particleIcon: "✦",
              particleColor: "#60a5fa",
              particles: [
                {left: "12%", top: "18%", delay: 0, size: "9px"},
                {left: "72%", top: "12%", delay: 0.7, size: "7px"},
                {left: "45%", top: "55%", delay: 0.3, size: "10px"},
                {left: "82%", top: "65%", delay: 1.0, size: "6px"},
                {left: "28%", top: "78%", delay: 0.5, size: "8px"},
                {left: "58%", top: "30%", delay: 1.4, size: "7px"},
                {left: "90%", top: "40%", delay: 0.2, size: "6px"},
              ],
            }, {
              typeBadge: t("landing.profiles_cards.personal_badge"),
              fullLabel: t("landing.profiles_cards.personal_label"),
              color: "#22c55e",
              amount: "14,392.55 €",
              transactions: 48,
              categories: 8,
              rotation: { base: "rotate(4deg)", md: "rotate(6deg)" },
              top: { base: "4%", md: "-2%" },
              left: { base: "28%", md: "28%" },
              zIndex: 2,
              gradient: "linear-gradient(135deg, #071a12 0%, #0b2e1a 30%, #0f3a22 60%, #071a12 100%)",
              glowColor: "rgba(34, 197, 94, 0.15)",
              particleIcon: "●",
              particleColor: "#4ade80",
              particles: [
                {left: "18%", top: "22%", delay: 0, size: "6px"},
                {left: "65%", top: "15%", delay: 0.6, size: "5px"},
                {left: "40%", top: "50%", delay: 0.2, size: "7px"},
                {left: "85%", top: "58%", delay: 0.9, size: "5px"},
                {left: "25%", top: "72%", delay: 0.4, size: "6px"},
                {left: "55%", top: "35%", delay: 1.2, size: "4px"},
                {left: "78%", top: "80%", delay: 0.7, size: "5px"},
              ],
            }, {
              typeBadge: t("landing.profiles_cards.couple_badge"),
              fullLabel: t("landing.profiles_cards.couple_label"),
              color: "#ec4899",
              amount: "27,154.30 €",
              transactions: 74,
              categories: 13,
              rotation: { base: "rotate(-2deg)", md: "rotate(-2deg)" },
              top: { base: "36%", md: "32%" },
              left: { base: "10%", md: "10%" },
              zIndex: 3,
              gradient: "linear-gradient(135deg, #1a0a16 0%, #2e0f24 30%, #3a1230 60%, #1a0a16 100%)",
              glowColor: "rgba(236, 72, 153, 0.18)",
              particleIcon: "♥",
              particleColor: "#f472b6",
              particles: [
                {left: "10%", top: "20%", delay: 0, size: "9px"},
                {left: "55%", top: "10%", delay: 0.5, size: "7px"},
                {left: "30%", top: "48%", delay: 0.2, size: "10px"},
                {left: "78%", top: "55%", delay: 0.8, size: "8px"},
                {left: "48%", top: "75%", delay: 0.4, size: "7px"},
                {left: "85%", top: "25%", delay: 1.1, size: "6px"},
                {left: "20%", top: "85%", delay: 0.6, size: "8px"},
                {left: "68%", top: "40%", delay: 1.3, size: "6px"},
              ],
            }].map((p, i) => {
              const isHovered = hoveredCard === i;
              const isFaded = hoveredCard !== null && hoveredCard !== i;
              return (
                <Box
                  key={i}
                  position="absolute"
                  top={p.top}
                  left={p.left}
                  w={{ base: "68%", md: "300px" }}
                  h={{ base: "140px", md: "175px" }}
                  bg={p.gradient}
                  border="1px solid"
                  borderColor={isHovered ? `${p.color}66` : `${p.color}30`}
                  borderRadius={{ base: "14px", md: "18px" }}
                  p={{ base: "12px 14px", md: "18px 24px" }}
                  zIndex={p.zIndex}
                  transform={p.rotation}
                  animation={isInView ? `${fadeSlideLeft} 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${0.4 + i * 0.2}s both` : "none"}
                  transition="border-color 0.5s ease, box-shadow 0.5s ease"
                  onMouseEnter={() => setHoveredCard(i)}
                  onMouseLeave={() => setHoveredCard(null)}
                  cursor="default"
                  overflow="hidden"
                  boxShadow={isHovered
                    ? `0 20px 50px rgba(0,0,0,0.6), 0 0 30px ${p.glowColor}, 0 8px 20px rgba(0,0,0,0.4), inset 0 0 30px ${p.color}08`
                    : `0 10px 35px rgba(0,0,0,0.5), 0 0 15px ${p.glowColor}`
                  }
                >
                  {/* Animated border glow overlay on hover */}
                  <Box
                    position="absolute" top="-1px" left="-1px" right="-1px" bottom="-1px"
                    borderRadius={{ base: "14px", md: "18px" }}
                    border="1px solid"
                    borderColor={p.color}
                    opacity={isHovered ? 1 : 0}
                    animation={isHovered ? `${cardBorderPulse} 2s ease-in-out infinite` : "none"}
                    transition="opacity 0.5s ease"
                    pointerEvents="none"
                    zIndex={5}
                  />

                  {/* Floating contextual particles on hover — staggered smooth appearance */}
                  {p.particles.map((pp, pi) => (
                    <Box
                      key={pi}
                      position="absolute"
                      top={pp.top}
                      left={pp.left}
                      fontSize={pp.size}
                      color={p.particleColor}
                      opacity={isHovered ? 0.7 : 0}
                      transform={isHovered ? "scale(1)" : "scale(0.5)"}
                      animation={isHovered ? `${particlePulse} ${3 + (pi % 3) * 0.8}s ease-in-out ${0.6 + pp.delay}s infinite` : "none"}
                      transition={`opacity 0.8s ease ${pi * 0.15}s, transform 0.8s ease ${pi * 0.15}s`}
                      pointerEvents="none"
                      zIndex={4}
                      filter="blur(0.3px)"
                    >
                      {p.particleIcon}
                    </Box>
                  ))}

                  {/* Ambient glow — intensifies on hover */}
                  <Box
                    position="absolute" top="-30%" right="-15%"
                    w="120px" h="120px" borderRadius="full"
                    bg={`radial-gradient(circle, ${p.color}${isHovered ? '30' : '18'}, transparent 70%)`}
                    filter="blur(25px)" pointerEvents="none"
                    transition="all 0.6s ease"
                  />
                  {/* Bottom edge glow */}
                  <Box
                    position="absolute" bottom="-10px" left="20%" right="20%"
                    h="40px"
                    bg={`radial-gradient(ellipse, ${p.color}${isHovered ? '22' : '12'}, transparent 70%)`}
                    filter="blur(15px)" pointerEvents="none"
                    transition="all 0.6s ease"
                  />

                  {/* Top row: Chip (with shine on hover) + Type badge (pulses on hover) */}
                  <Flex justify="space-between" align="center" mb={{ base: "6px", md: "12px" }} position="relative" zIndex={2}>
                    {/* Gold chip */}
                    <Box
                      w={{ base: "28px", md: "36px" }} h={{ base: "22px", md: "28px" }} borderRadius="5px"
                      bg="linear-gradient(135deg, #d4af37 0%, #f5d77a 50%, #c9a227 100%)"
                      boxShadow={isHovered
                        ? "inset 0 1px 2px rgba(255,255,255,0.4), 0 1px 6px rgba(212,175,55,0.4)"
                        : "inset 0 1px 2px rgba(255,255,255,0.3), 0 1px 3px rgba(0,0,0,0.3)"}
                      position="relative" overflow="hidden"
                      transition="box-shadow 0.5s ease"
                    >
                      <Box position="absolute" top="6px" left="3px" right="3px" h="1px" bg="rgba(0,0,0,0.12)" />
                      <Box position="absolute" top="12px" left="3px" right="3px" h="1px" bg="rgba(0,0,0,0.12)" />
                      <Box position="absolute" top="18px" left="3px" right="3px" h="1px" bg="rgba(0,0,0,0.12)" />
                      <Box position="absolute" left="12px" top="3px" bottom="3px" w="1px" bg="rgba(0,0,0,0.08)" />
                      <Box position="absolute" left="24px" top="3px" bottom="3px" w="1px" bg="rgba(0,0,0,0.08)" />
                      {/* Shine sweep on hover */}
                      <Box
                        position="absolute" top="0" bottom="0" w="50%"
                        bg="linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)"
                        left="-40%"
                        animation={isHovered ? `${chipShine} 1.5s ease-in-out infinite 0.3s` : "none"}
                        opacity={isHovered ? 1 : 0}
                        transition="opacity 0.3s ease"
                        pointerEvents="none"
                      />
                    </Box>
                    {/* Type badge */}
                    <Box
                      bg={isHovered ? `${p.color}20` : "rgba(255,255,255,0.08)"}
                      border="1px solid"
                      borderColor={isHovered ? `${p.color}50` : "rgba(255,255,255,0.12)"}
                      borderRadius="6px"
                      px={{ base: "6px", md: "10px" }} py={{ base: "2px", md: "3px" }}
                      transition="all 0.5s ease"
                      color={p.color}
                      animation={isHovered ? `${badgePulse} 2.5s ease-in-out infinite` : "none"}
                    >
                      <Text fontSize={{ base: "7px", md: "8px" }} fontWeight="bold" color={isHovered ? p.color : "rgba(255,255,255,0.7)"} letterSpacing="1.5px" textTransform="uppercase" transition="color 0.5s ease">
                        {p.typeBadge}
                      </Text>
                    </Box>
                  </Flex>

                  {/* Balance — glow on hover */}
                  <Box mb={{ base: "4px", md: "10px" }} position="relative" zIndex={2}>
                    <Text fontSize={{ base: "7px", md: "8px" }} color={isHovered ? `${p.color}aa` : "rgba(255,255,255,0.4)"} letterSpacing="2px" textTransform="uppercase" mb="2px" transition="color 0.5s ease">
                      {t("landing.profiles_cards.balance")}
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "2xl" }}
                      fontWeight="700"
                      color={isHovered ? p.color : "white"}
                      letterSpacing="-0.5px"
                      fontFamily="'SF Mono', 'Roboto Mono', monospace"
                      lineHeight="1"
                      transition="color 0.5s ease, text-shadow 0.5s ease"
                      textShadow={isHovered ? `0 0 15px ${p.color}60` : "none"}
                    >
                      {p.amount}
                    </Text>
                  </Box>

                  {/* Bottom: Name + Stats */}
                  <Flex justify="space-between" align="flex-end" position="relative" zIndex={2}>
                    <Box>
                      <Text
                        fontSize={{ base: "8px", md: "11px" }}
                        fontWeight="700"
                        color={isHovered ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.6)"}
                        letterSpacing="1.5px"
                        textTransform="uppercase"
                        mb="2px"
                        transition="color 0.5s ease"
                      >
                        {p.fullLabel}
                      </Text>
                      <HStack spacing={{ base: 1, md: 3 }}>
                        <Text fontSize={{ base: "8px", md: "10px" }} color={isHovered ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.35)"} transition="color 0.5s ease">
                          <Text as="span" color={isHovered ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.55)"} fontWeight="600" transition="color 0.5s ease">{p.transactions}</Text> {t("landing.profiles_cards.transactions")}
                        </Text>
                        <Text fontSize={{ base: "8px", md: "10px" }} color={isHovered ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.35)"} transition="color 0.5s ease">
                          <Text as="span" color={isHovered ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.55)"} fontWeight="600" transition="color 0.5s ease">{p.categories}</Text> {t("landing.profiles_cards.categories")}
                        </Text>
                      </HStack>
                    </Box>
                    {/* Action icon — lights up on hover */}
                    <HStack spacing={1}>
                      <Flex
                        w={{ base: "22px", md: "28px" }} h={{ base: "22px", md: "28px" }} borderRadius="8px" align="center" justify="center"
                        bg={isHovered ? "rgba(34, 197, 94, 0.3)" : "rgba(34, 197, 94, 0.18)"}
                        border="1px solid"
                        borderColor={isHovered ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.25)"}
                        transition="all 0.5s ease"
                        boxShadow={isHovered ? "0 0 10px rgba(34, 197, 94, 0.3)" : "none"}
                      >
                        <Icon as={FaExchangeAlt} w="10px" h="10px" color="green.400" />
                      </Flex>
                    </HStack>
                  </Flex>
                </Box>
              );
            })}
          </Box>
        ) : isMobile ? (
          <Box
            position="relative"
            zIndex={0}
            mx="auto"
            maxW="280px"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              perspective: "600px",
              transformStyle: "preserve-3d",
              transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transition: "transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 1.2s ease, border-color 1.2s ease",
            }}
            bg="linear-gradient(160deg, #1e1e2a 0%, #161624 50%, #121220 100%)"
            borderRadius="32px"
            p="8px"
            border="1px solid"
            borderColor={tilt.active ? "rgba(6, 182, 212, 0.2)" : "rgba(255, 255, 255, 0.07)"}
            boxShadow={tilt.active
              ? "0 0 0 1px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 25px 60px rgba(6, 182, 212, 0.22), 0 0 35px rgba(6, 182, 212, 0.12)"
              : "0 0 0 1px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 20px 50px rgba(6, 182, 212, 0.15), 0 0 25px rgba(6, 182, 212, 0.08)"}
            animation={`${deviceFloat} 5s ease-in-out infinite`}
            cursor="default"
          >
            {/* Screen */}
            <Box borderRadius="24px" overflow="hidden" position="relative" bg="#000">
              <img
                src={image}
                alt=""
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {/* Screen glare */}
              <Box
                position="absolute" top="0" left="0" right="0" bottom="0"
                zIndex={1}
                pointerEvents="none"
                overflow="hidden"
                borderRadius="24px"
                _after={{
                  content: '""',
                  position: "absolute",
                  top: "-20%", left: "-20%",
                  w: "40%", h: "140%",
                  background: "linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.06), rgba(255, 255, 255, 0.08), rgba(6, 182, 212, 0.06), transparent)",
                  animation: `${screenGlare} 5s ease-in-out infinite 2.5s`,
                }}
              />

            </Box>
          </Box>
        ) : (
        <Box
          borderRadius={isDashboard ? "24px" : "32px"}
          overflow="hidden"
          position="relative"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={(isMagic || isDashboard) ? {
            perspective: "800px",
            transformStyle: "preserve-3d",
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            transition: tilt.active
              ? "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.5s ease, box-shadow 0.5s ease"
              : "transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.5s ease, box-shadow 0.5s ease",
            cursor: "default",
          } : undefined}
          bg={isMagic
            ? "linear-gradient(135deg, rgba(15, 20, 30, 0.9) 0%, rgba(30, 20, 50, 0.7) 100%)"
            : isCategories
              ? "linear-gradient(135deg, rgba(15, 20, 35, 0.9) 0%, rgba(20, 25, 50, 0.6) 100%)"
              : "linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 45, 0.5) 100%)"}
          border="1px solid"
          borderColor={isMagic ? (tilt.active ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.22)")
            : isDashboard ? (tilt.active ? `${accentColor}44` : `${accentColor}22`)
            : isCategories ? "rgba(59, 130, 246, 0.25)"
            : `${accentColor}22`}
          boxShadow={isMagic
            ? (tilt.active
              ? "0 25px 70px rgba(139, 92, 246, 0.2), 0 0 40px rgba(139, 92, 246, 0.1)"
              : "0 20px 60px rgba(139, 92, 246, 0.12), 0 0 30px rgba(139, 92, 246, 0.06)")
            : isDashboard
              ? (tilt.active
                ? `0 30px 70px rgba(0, 0, 0, 0.6), 0 0 40px ${accentColor}20`
                : `0 25px 60px rgba(0, 0, 0, 0.5), 0 0 30px ${accentColor}15`)
              : isCategories
                ? "0 20px 50px rgba(59, 130, 246, 0.15), 0 0 30px rgba(59, 130, 246, 0.08)"
                : undefined}
          transition="border-color 0.5s ease, box-shadow 0.5s ease"
          _before={{
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0,
            h: "1px",
            background: isMagic
              ? "linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.35), rgba(99, 102, 241, 0.25), transparent)"
              : isCategories
                ? "linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), rgba(96, 165, 250, 0.3), transparent)"
                : `linear-gradient(90deg, transparent, ${accentColor}44, transparent)`,
            zIndex: 2,
          }}
        >
          {/* Accent glow behind image */}
          <Box
            position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
            w="70%" h="70%" borderRadius="full"
            bg={isMagic
              ? "radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.05) 40%, transparent 70%)"
              : `radial-gradient(circle, ${accentColor}15 0%, transparent 70%)`}
            filter="blur(40px)"
            pointerEvents="none"
          />

          {/* Categories: subtle data grid overlay */}
          {isCategories && (
            <>
              <Box
                position="absolute" top="0" left="0" right="0" bottom="0"
                backgroundImage="linear-gradient(rgba(59, 130, 246, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.06) 1px, transparent 1px)"
                backgroundSize="50px 50px"
                animation={`${gridPulse} 4s ease-in-out infinite`}
                pointerEvents="none"
                zIndex={1}
              />
              {/* Animated accent bars */}
              {[20, 40, 60, 80].map((top, i) => (
                <Box
                  key={i}
                  position="absolute" top={`${top}%`} left="5%"
                  w={`${30 + i * 12}%`} h="2px"
                  bg={`linear-gradient(90deg, rgba(59, 130, 246, ${0.12 - i * 0.02}) 0%, transparent 100%)`}
                  animation={isInView ? `${barGrow} 1.2s ease-out ${0.8 + i * 0.15}s both` : "none"}
                  transformOrigin="left"
                  pointerEvents="none"
                  zIndex={1}
                />
              ))}
              {/* Data shimmer line */}
              <Box
                position="absolute" top="0" left="0" right="0" bottom="0"
                bg="linear-gradient(90deg, transparent 40%, rgba(59, 130, 246, 0.04) 48%, rgba(96, 165, 250, 0.08) 50%, rgba(59, 130, 246, 0.04) 52%, transparent 60%)"
                backgroundSize="300% 100%"
                animation={`${dataShimmer} 6s linear infinite 3s`}
                pointerEvents="none"
                zIndex={1}
              />
            </>
          )}

          {/* Magic shimmer sweep */}
          {isMagic && (
            <Box
              position="absolute" top="0" left="0" right="0" bottom="0"
              zIndex={1}
              pointerEvents="none"
              _after={{
                content: '""',
                position: "absolute",
                top: "-50%", left: "-50%",
                w: "200%", h: "200%",
                background: "linear-gradient(90deg, transparent 30%, rgba(139, 92, 246, 0.08) 45%, rgba(255, 255, 255, 0.1) 50%, rgba(139, 92, 246, 0.08) 55%, transparent 70%)",
                animation: `${magicShimmer} 4s ease-in-out infinite 2s`,
              }}
            />
          )}



          {/* Dashboard: shimmer sweep + cursor spotlight */}
          {isDashboard && (
            <>
              <Box
                position="absolute" top="0" left="0" right="0" bottom="0"
                zIndex={1}
                pointerEvents="none"
                _after={{
                  content: '""',
                  position: "absolute",
                  top: "-50%", left: "-50%",
                  w: "200%", h: "200%",
                  background: "linear-gradient(90deg, transparent 30%, rgba(34, 197, 94, 0.05) 45%, rgba(255, 255, 255, 0.07) 50%, rgba(34, 197, 94, 0.05) 55%, transparent 70%)",
                  animation: `${magicShimmer} 6s ease-in-out infinite 3s`,
                }}
              />

            </>
          )}

          {/* Magic variant: fake AI report preview */}
          {isMagic ? (
            <Box position="relative" zIndex={0} p={{ base: "20px", md: "28px" }} minH="340px">
              {/* Header */}
              <Flex align="center" gap="12px" mb="20px">
                <Flex
                  w="40px" h="40px"
                  bgGradient="linear(to-br, purple.500, blue.500)"
                  borderRadius="12px"
                  align="center" justify="center"
                  boxShadow="0 6px 20px rgba(139, 92, 246, 0.4)"
                >
                  <Icon as={FaRobot} color="white" w={4} h={4} />
                </Flex>
                <Box>
                  <Text fontSize="sm" color="#fff" fontWeight="700" letterSpacing="-0.01em">
                    {t("landing.fake_report.header_title")}
                  </Text>
                  <Text fontSize="10px" color="gray.500">
                    {t("landing.fake_report.header_subtitle")}
                  </Text>
                </Box>
              </Flex>

              {/* Health Score Section */}
              <Box
                p="18px"
                borderRadius="14px"
                mb="18px"
                bg="rgba(139, 92, 246, 0.06)"
                border="1px solid rgba(139, 92, 246, 0.12)"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute" top="-20px" right="-20px"
                  w="80px" h="80px" borderRadius="full"
                  bg="purple.500" opacity={0.08} filter="blur(30px)"
                />
                <Text
                  color="gray.400" fontSize="9px" fontWeight="700"
                  textTransform="uppercase" letterSpacing="2px" mb="12px"
                >
                  {t("landing.fake_report.health_title")}
                </Text>
                <Flex align="center" gap="16px">
                  {/* Circular Score */}
                  <Box position="relative" w="70px" h="70px" flexShrink={0}>
                    <svg width="70" height="70" viewBox="-5 -5 70 70">
                      <circle cx="30" cy="30" r="25" fill="none" stroke="rgba(139, 92, 246, 0.1)" strokeWidth="6" />
                      <circle cx="30" cy="30" r="25" fill="none" stroke="url(#fakeScoreGrad)" strokeWidth="6" strokeLinecap="round" strokeDasharray="132 157" transform="rotate(-90 30 30)" style={{ filter: "drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))" }} />
                      <defs>
                        <linearGradient id="fakeScoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#3B82F6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <Flex position="absolute" inset="0" align="center" justify="center" direction="column">
                      <Text fontSize="lg" fontWeight="800" bgGradient="linear(to-r, purple.400, blue.400)" bgClip="text" lineHeight="1">84</Text>
                      <Text color="gray.500" fontSize="8px" fontWeight="500">/100</Text>
                    </Flex>
                  </Box>
                  <Text color="gray.400" fontSize="xs" lineHeight="1.6">
                    {t("landing.fake_report.health_desc")}
                  </Text>
                </Flex>
              </Box>

              {/* Recommendations */}
              <Box mb="0">
                <Flex align="center" gap="6px" mb="12px">
                  <Icon as={IoSparkles} color="purple.400" w={3.5} h={3.5} />
                  <Text color="#fff" fontSize="xs" fontWeight="700">
                    {t("landing.fake_report.reco_title")}
                  </Text>
                </Flex>

                {/* Recommendation 1 */}
                <Box
                  p="14px" mb="10px"
                  bg="rgba(255,255,255,0.03)"
                  borderRadius="12px"
                  borderLeft="3px solid"
                  borderLeftColor="green.400"
                >
                  <Flex align="center" gap="8px" mb="4px">
                    <Icon as={IoCheckmarkCircle} color="green.400" w={4} h={4} />
                    <Text color="#fff" fontSize="xs" fontWeight="600">
                      {t("landing.fake_report.reco1_title")}
                    </Text>
                  </Flex>
                  <Text color="gray.500" fontSize="11px" pl="24px" lineHeight="1.5">
                    {t("landing.fake_report.reco1_desc")}
                  </Text>
                </Box>

                {/* Recommendation 2 — easter egg */}
                <Box
                  p="14px"
                  bg="rgba(255,255,255,0.03)"
                  borderRadius="12px"
                  borderLeft="3px solid"
                  borderLeftColor="purple.400"
                >
                  <Flex align="center" gap="8px" mb="4px">
                    <Icon as={IoSparkles} color="purple.400" w={4} h={4} />
                    <Text color="#fff" fontSize="xs" fontWeight="600">
                      {t("landing.fake_report.reco2_title")}
                    </Text>
                  </Flex>
                  <Text color="gray.500" fontSize="11px" pl="24px" lineHeight="1.5">
                    {t("landing.fake_report.reco2_desc")}
                  </Text>
                </Box>
              </Box>

              {/* Bottom fade-out */}
              <Box
                position="absolute" bottom="0" left="0" right="0"
                h="100px"
                bg="linear-gradient(to top, rgba(15, 20, 30, 1) 0%, rgba(15, 20, 30, 0.95) 30%, transparent 100%)"
                pointerEvents="none"
                zIndex={2}
                borderRadius="0 0 32px 32px"
              />
            </Box>
          ) : (
            <>
              {/* Screenshot */}
              <img
                src={image}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  position: "relative",
                  zIndex: 0,
                  ...(isDashboard ? { borderRadius: "24px" } : {}),
                }}
              />

              {/* Bottom edge gradient for dashboard to fade into the dark bg */}
              {isDashboard && (
                <Box
                  position="absolute" bottom="0" left="0" right="0"
                  h="60px"
                  bg="linear-gradient(to top, rgba(10, 13, 20, 0.8) 0%, transparent 100%)"
                  pointerEvents="none"
                  zIndex={1}
                />
              )}
            </>
          )}
        </Box>
        )}
      </Box>
    </Flex>
  );
};

// ============================================
// STAT ITEM COMPONENT
// ============================================

// ============================================
// LIVE STATS BAR — real numbers as social proof
// Discreet single-line sentence shown inside the hero
// ============================================

const LiveStatsBar = ({ stats, t, i18n, visible = true }) => {
  if (!stats || stats.users == null) return null;
  const locale = i18n.language === "fr" ? "fr-FR" : i18n.language || "en";
  const fmt = (n) => Number(n).toLocaleString(locale);
  const lastUpdated = stats.lastUpdated
    ? new Date(stats.lastUpdated).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <VStack
      spacing={1.5}
      mt={{ base: 10, md: 14 }}
      opacity={0}
      animation={visible ? `${heroRiseUp} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.35s forwards` : "none"}
    >
      <Text fontSize={{ base: "sm", md: "md" }} color="gray.400" fontWeight="500" textAlign="center" lineHeight="1.6">
        {t("landing.stats.social_prefix")}{" "}
        <Box as="span" color="#4ade80" fontWeight="700">{fmt(stats.users)}</Box>{" "}
        {t("landing.stats.users_label")}{" "}
        {t("landing.stats.social_mid")}{" "}
        <Box as="span" color="#4ade80" fontWeight="700">{fmt(stats.transactions)}</Box>{" "}
        {t("landing.stats.transactions_label")}
      </Text>
      {lastUpdated && (
        <Text fontSize={{ base: "10px", md: "11px" }} color="gray.600">
          {t("landing.stats.last_updated")} {lastUpdated}
        </Text>
      )}
    </VStack>
  );
};

const StatItem = ({ value, label, icon, icons, delay = 0, href }) => {
  const [ref, isInView] = useInView();
  const Wrapper = href ? Link : Box;
  const wrapperProps = href ? { href, isExternal: true, _hover: { textDecoration: "none", transform: "translateY(-2px)" }, transition: "transform 0.2s" } : {};
  return (
    <Wrapper {...wrapperProps}>
      <VStack
        ref={ref}
        spacing={1}
        animation={isInView ? `${numberCount} 0.6s ease-out ${delay}s both` : "none"}
        opacity={isInView ? 1 : 0}
      >
        {icons ? (
          <HStack spacing={3}>
            {icons.map((ic, i) => (
              <Icon key={i} as={ic} w={{ base: 4, md: 5 }} h={{ base: 4, md: 5 }} color="#4ade80" />
            ))}
          </HStack>
        ) : icon ? (
          <HStack spacing={2}>
            <Icon as={icon} w={{ base: 4, md: 5 }} h={{ base: 4, md: 5 }} color="#4ade80" />
            <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="white">
              {value}
            </Text>
          </HStack>
        ) : (
          <Text
            fontSize={{ base: "3xl", md: "5xl" }}
            fontWeight="extrabold"
            bgGradient="linear(to-r, #4ade80, #22c55e)"
            bgClip="text"
            lineHeight="1"
          >
            {value}
          </Text>
        )}
        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" textTransform="uppercase" letterSpacing="2px">
          {label}
        </Text>
      </VStack>
    </Wrapper>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

function LandingPage() {
  const { t, i18n } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [navSolid, setNavSolid] = useState(false);
  const [stats, setStats] = useState({ users: null, transactions: null, lastUpdated: null });
  const [heroPhase, setHeroPhase] = useState(0);

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? true;

  const contentRef = useRef(null);

  // Cinematic hero intro — title pops in centered, then the rest rises up
  useEffect(() => {
    const timers = [
      setTimeout(() => setHeroPhase(1), 200),
      setTimeout(() => setHeroPhase(2), 1150),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    const browserLang = getBrowserLanguage();
    if (browserLang !== 'en') i18n.changeLanguage(browserLang);
  }, [i18n]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          setNavSolid(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "unset"; };
  }, []);

  // Fetch real public stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/public-stats`);
        setStats(response.data);
      } catch {
        // Silently fail — stats just won't show
      }
    };
    fetchStats();
  }, []);

  const scrollToContent = useCallback(() => {
    if (contentRef.current) {
      const y = contentRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  // Scroll sections data — alternating left/right with different accent colors
  const scrollSections = [
    {
      align: "left",
      labelKey: "landing.scroll.s1_label",
      titleKey: "landing.scroll.s1_title",
      descKey: "landing.scroll.s1_desc",
      accentColor: "#22c55e",
      image: imgDashboard,
      variant: "dashboard",
    },
    {
      align: "right",
      labelKey: "landing.scroll.s4_label",
      titleKey: "landing.scroll.s4_title",
      descKey: "landing.scroll.s4_desc",
      accentColor: "#06b6d4",
      image: imgMobile,
      variant: "mobile",
    },
    {
      align: "left",
      labelKey: "landing.scroll.s3_label",
      titleKey: "landing.scroll.s3_title",
      descKey: "landing.scroll.s3_desc",
      accentColor: "#f59e0b",
      image: null,
      variant: "profiles",
    },
    {
      align: "right",
      labelKey: "landing.scroll.s2_label",
      titleKey: "landing.scroll.s2_title",
      descKey: "landing.scroll.s2_desc",
      accentColor: "#8b5cf6",
      image: null,
      variant: "magic",
    },
  ];

  return (
    <Box minH="100vh" w="100%" position="relative" bg="#0a0d14" overflowX="hidden">
      {/* ============================================ */}
      {/* ANIMATED BACKGROUND */}
      {/* ============================================ */}
      <Box position="fixed" top="0" left="0" right="0" bottom="0" zIndex="0" pointerEvents="none">
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.10) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.06) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        <Box
          position="absolute" top={`calc(10% - ${scrollY * 0.1}px)`} left="5%"
          w={{ base: "200px", md: "350px" }} h={{ base: "200px", md: "350px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(34, 197, 94, 0.12) 0%, transparent 70%)"
          filter="blur(60px)" animation={`${float} 10s ease-in-out infinite`}
        />
        <Box
          position="absolute" bottom={`calc(20% + ${scrollY * 0.05}px)`} right="5%"
          w={{ base: "250px", md: "450px" }} h={{ base: "250px", md: "450px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(74, 222, 128, 0.10) 0%, transparent 70%)"
          filter="blur(80px)" animation={`${floatReverse} 12s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
          w={{ base: "400px", md: "700px" }} h={{ base: "400px", md: "700px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 60%)"
          filter="blur(100px)" animation={`${pulse} 8s ease-in-out infinite`}
        />
        {/* Subtle grid */}
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0" opacity="0.025"
          backgroundImage="linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)"
          backgroundSize="80px 80px"
        />
        {/* Horizontal accent lines */}
        <Box
          position="absolute" top="25%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.15), transparent)"
          animation={`${shimmer} 4s linear infinite`} backgroundSize="200% 100%"
        />
        <Box
          position="absolute" top="75%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(74, 222, 128, 0.10), transparent)"
          animation={`${shimmer} 5s linear infinite 1.5s`} backgroundSize="200% 100%"
        />
      </Box>

      {/* ============================================ */}
      {/* NAVBAR — ultra minimal: logo + 2 buttons */}
      {/* ============================================ */}
      <Box
        position="fixed" top="0" left="0" right="0" zIndex="100"
        pt="env(safe-area-inset-top)"
        bg={navSolid
          ? "rgba(10, 13, 20, 0.85)"
          : "transparent"
        }
        backdropFilter={navSolid ? "blur(24px) saturate(1.4)" : "none"}
        borderBottom={navSolid ? "1px solid rgba(255, 255, 255, 0.04)" : "none"}
        transition="all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <Box w="100%">
          <Flex h={{ base: "56px", md: "80px" }} align="center" justify="space-between" px={{ base: 4, md: 8 }} mt={{ base: "6px", md: 0 }}>
            <HStack spacing={2} animation={`${slideInLeft} 1s ease-out`} flexShrink={0}>
              <Box position="relative">
                <Box
                  position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
                  w="44px" h="44px" borderRadius="full"
                  bg="radial-gradient(circle, rgba(34, 197, 94, 0.25) 0%, transparent 70%)" filter="blur(8px)"
                />
                <img src={logo} width={isMobile ? "26px" : "40px"} alt="Mufyn Logo" style={{ position: "relative", zIndex: 1 }} />
              </Box>
              <Heading fontSize={{ base: "sm", md: "xl" }} fontWeight="bold" color="white" letterSpacing="-0.5px">
                {config.appName}
              </Heading>
            </HStack>

            <HStack spacing={{ base: "6px", md: 3 }} animation={`${slideInRight} 1s ease-out`}>
              <MufynButton as={RouterLink} to="/auth/faq" variant="ghost" size={isMobile ? "xs" : "sm"} px={{ base: "8px", md: 4 }} fontSize={{ base: "11px", md: "sm" }} h={{ base: "30px", md: "auto" }}>
                FAQ
              </MufynButton>
              <MufynButton as={RouterLink} to="/auth/signin" variant="primary" size={isMobile ? "xs" : "sm"} px={{ base: "10px", md: 4 }} fontSize={{ base: "11px", md: "sm" }} h={{ base: "30px", md: "auto" }}>
                {t("landing.nav.signin")}
              </MufynButton>
            </HStack>
          </Flex>
        </Box>
      </Box>

      {/* ============================================ */}
      {/* HERO — full viewport, cinematic */}
      {/* ============================================ */}
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex="1"
      >
        {/* Hero background image with green overlay */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          overflow="hidden"
          zIndex="0"
          pointerEvents="none"
          opacity="0"
          animation={`${heroImageReveal} 2s ease-out 1.2s forwards`}
        >
          <Box
            as="img"
            src={heroBackground}
            alt=""
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            objectFit="cover"
            objectPosition="center"
            animation={`${heroImageDrift} 30s ease-in-out infinite`}
            willChange="transform"
          />
          {/* Green-tinted overlay */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="linear-gradient(180deg, rgba(10, 13, 20, 0.88) 0%, rgba(12, 45, 25, 0.86) 40%, rgba(8, 35, 20, 0.88) 70%, rgba(10, 13, 20, 0.94) 100%)"
          />
        </Box>

        {/* Subtle ripple rings (skip first, keep delayed ones) */}
        {[1, 2].map((i) => (
          <Box
            key={i}
            position="absolute" top="50%" left="50%"
            transform="translate(-50%, -50%)"
            w="350px" h="350px" borderRadius="full"
            border={`1px solid rgba(34, 197, 94, ${0.10 - i * 0.025})`}
            opacity={0}
            animation={`${ripple} 5s ease-out infinite ${i * 1.5}s`}
            pointerEvents="none"
          />
        ))}

        <Container maxW="container.xl" px={{ base: 5, md: 6 }} position="relative" zIndex={1}>
          <Flex direction="column" align="center" textAlign="center">
            {/* Hero title group — pops in centered, then rises to its place */}
            <Box
              transform={heroPhase >= 2 ? "translateY(0)" : "translateY(14vh)"}
              transition="transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)"
              willChange="transform"
            >
              {/* Main heading — Mufyn */}
              <Heading
                as="h1"
                fontSize={{ base: "72px", sm: "8xl", md: "9xl", lg: "9xl" }}
                fontWeight="extrabold"
                lineHeight={{ base: "1.15", md: "1.1" }}
                pb={{ base: 1, md: 3 }}
                mb={{ base: 3, md: 4 }}
                letterSpacing={{ base: "-2px", md: "-5px" }}
                bgGradient="linear(to-r, #4ade80, #22c55e, #10b981)"
                bgClip="text"
                backgroundSize="200% auto"
                opacity={0}
                animation={heroPhase >= 1
                  ? `${heroPopIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${gradientMove} 5s linear infinite`
                  : "none"}
              >
                {config.appName}
              </Heading>
            </Box>

            {/* Slogan — direct, no fluff */}
            <Text
              fontSize={{ base: "2xl", sm: "3xl", md: "4xl" }}
              color="white"
              fontWeight="700"
              maxW="720px"
              mb={{ base: 10, md: 14 }}
              lineHeight="1.2"
              letterSpacing={{ base: "-0.5px", md: "-1px" }}
              opacity={0}
              animation={heroPhase >= 2 ? `${heroRiseUp} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s forwards` : "none"}
            >
              {t("landing.hero.title_part1")} {t("landing.hero.title_part2")}
            </Text>

            {/* CTA row — clean primary + Play Store */}
            <Flex
              direction={{ base: "column", sm: "row" }}
              align="center"
              gap={{ base: 3, sm: 4 }}
              w={{ base: "100%", sm: "auto" }}
              maxW={{ base: "340px", sm: "none" }}
              opacity={0}
              animation={heroPhase >= 2 ? `${heroRiseUp} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards` : "none"}
            >
              {/* Primary CTA */}
              <Box
                as={RouterLink}
                to="/auth/signup"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={3}
                w={{ base: "100%", sm: "auto" }}
                px={{ base: 7, md: 9 }}
                h={{ base: "54px", md: "60px" }}
                borderRadius="16px"
                bg="linear-gradient(135deg, #22c55e 0%, #10b981 100%)"
                color="white"
                fontFamily="'Poppins', sans-serif"
                fontWeight="700"
                fontSize={{ base: "md", md: "lg" }}
                letterSpacing="0.02em"
                textDecoration="none"
                boxShadow="0 0 0 1px rgba(74,222,128,0.4), 0 10px 40px rgba(34,197,94,0.35)"
                transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                sx={{
                  "& .cta-arrow": { transition: "transform 0.3s ease" },
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 0 0 1px rgba(74,222,128,0.6), 0 16px 50px rgba(34,197,94,0.5)",
                    background: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                    textDecoration: "none",
                  },
                  "&:hover .cta-arrow": { transform: "translateX(4px)" },
                  "&:active": { transform: "translateY(-1px)" },
                }}
              >
                {t("landing.hero.cta_try")}
                <Icon as={FaArrowRight} className="cta-arrow" w={4} h={4} />
              </Box>

              {/* Secondary CTA — Play Store */}
              <Link
                href={config.playStoreUrl}
                isExternal
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2.5}
                w={{ base: "100%", sm: "auto" }}
                px={{ base: 7, md: 8 }}
                h={{ base: "54px", md: "60px" }}
                borderRadius="16px"
                border="1px solid rgba(255,255,255,0.12)"
                bg="rgba(255,255,255,0.03)"
                backdropFilter="blur(10px)"
                color="gray.200"
                fontWeight="600"
                fontSize={{ base: "sm", md: "md" }}
                textDecoration="none"
                transition="all 0.3s ease"
                _hover={{
                  textDecoration: "none",
                  borderColor: "rgba(74,222,128,0.4)",
                  bg: "rgba(34,197,94,0.06)",
                  color: "white",
                }}
              >
                <Icon as={FaGooglePlay} w={4} h={4} color="#4ade80" />
                {t("landing.hero.cta_playstore")}
              </Link>
            </Flex>

            {/* Live stats ticker — real numbers from the API */}
            <LiveStatsBar stats={stats} t={t} i18n={i18n} visible={heroPhase >= 2} />
          </Flex>
        </Container>

        {/* Scroll indicator — absolutely positioned so it doesn't shift hero centering */}
        <Box
          position="absolute"
          bottom={{ base: "24px", md: "40px" }}
          left="50%"
          transform="translateX(-50%)"
          opacity={heroPhase >= 2 ? undefined : 0}
          animation={heroPhase >= 2 ? `${bounceArrow} 2.5s ease-in-out infinite` : "none"}
          cursor="pointer"
          onClick={scrollToContent}
        >
          <Icon as={FaArrowDown} w={4} h={4} color="gray.600" />
        </Box>
      </Box>

      {/* ============================================ */}
      {/* STATS BAR — real data from API + platform info */}
      {/* (hidden for now — uncomment display when stats are worth showing) */}
      {/* ============================================ */}
      <Box ref={contentRef} position="relative" zIndex="1" py={{ base: 10, md: 14 }}
        borderTop="1px solid rgba(255,255,255,0.04)"
        borderBottom="1px solid rgba(255,255,255,0.04)"
        display="none"
      >
        <Container maxW="container.lg">
          <Flex justify="space-around" align="center" wrap="wrap" gap={8}>
            {/* Real user count */}
            <StatItem
              value={stats.users !== null ? stats.users.toLocaleString() : "—"}
              label={t("landing.stats.users_label")}
              delay={0}
            />
            <Box w="1px" h="40px" bg="rgba(255,255,255,0.06)" display={{ base: "none", md: "block" }} />

            {/* Real transaction count */}
            <StatItem
              value={stats.transactions !== null ? stats.transactions.toLocaleString() : "—"}
              label={t("landing.stats.transactions_label")}
              delay={0.1}
            />
            <Box w="1px" h="40px" bg="rgba(255,255,255,0.06)" display={{ base: "none", md: "block" }} />

            {/* Platform: Android — links to Play Store */}
            <StatItem
              value="Play Store"
              label={t("landing.stats.android_label")}
              icon={FaGooglePlay}
              delay={0.2}
              href={config.playStoreUrl}
            />
            <Box w="1px" h="40px" bg="rgba(255,255,255,0.06)" display={{ base: "none", md: "block" }} />

            {/* Multi-device availability */}
            <StatItem
              label={t("landing.stats.devices_label")}
              icons={[FaDesktop, FaTabletAlt, FaMobileAlt]}
              delay={0.3}
            />
          </Flex>

          {/* Live indicator + last updated */}
          {stats.lastUpdated && (
            <Flex justify="center" mt={5} align="center" gap={2}>
              <Box w="6px" h="6px" borderRadius="full" bg="#22c55e" boxShadow="0 0 8px #22c55e" />
              <Text fontSize="xs" color="gray.600">
                {t("landing.stats.live")} — {t("landing.stats.last_updated")} {new Date(stats.lastUpdated).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
              </Text>
            </Flex>
          )}
        </Container>
      </Box>

      {/* ============================================ */}
      {/* MANIFESTO — "pas une app de plus" */}
      {/* ============================================ */}
      {(() => {
        const [maniRef, maniInView] = useInView({ threshold: 0.15 });
        return (
          <Box
            ref={maniRef}
            position="relative"
            zIndex="1"
            py={{ base: 20, md: 32 }}
            overflow="hidden"
          >
            {/* Subtle glow */}
            <Box
              position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
              w="600px" h="600px" borderRadius="full"
              bg="radial-gradient(circle, rgba(34, 197, 94, 0.06) 0%, transparent 60%)"
              filter="blur(100px)" pointerEvents="none"
            />

            <Container maxW="container.md" position="relative">
              {/* Intro line */}
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="4px"
                color="#22c55e"
                textAlign="center"
                mb={6}
                opacity={maniInView ? 1 : 0}
                animation={maniInView ? `${heroReveal} 0.8s ease-out both` : "none"}
              >
                {t("landing.manifesto.badge")}
              </Text>

              {/* Main statement */}
              <Heading
                fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                fontWeight="extrabold"
                color="white"
                textAlign="center"
                lineHeight="1.2"
                mb={8}
                letterSpacing="-1px"
                opacity={maniInView ? 1 : 0}
                animation={maniInView ? `${heroReveal} 0.8s ease-out 0.1s both` : "none"}
              >
                {t("landing.manifesto.title")}
              </Heading>

              {/* Paragraphs */}
              <VStack spacing={5} maxW="640px" mx="auto">
                {["p1", "p2", "p3"].map((key, i) => (
                  <Text
                    key={key}
                    fontSize={{ base: "sm", md: "md" }}
                    color="gray.400"
                    textAlign="center"
                    lineHeight="1.8"
                    opacity={maniInView ? 1 : 0}
                    animation={maniInView ? `${heroReveal} 0.8s ease-out ${0.2 + i * 0.1}s both` : "none"}
                  >
                    {t(`landing.manifesto.${key}`)}
                  </Text>
                ))}
              </VStack>

              {/* Signature line */}
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="#4ade80"
                fontWeight="semibold"
                textAlign="center"
                mt={10}
                fontStyle="italic"
                opacity={maniInView ? 1 : 0}
                animation={maniInView ? `${heroReveal} 0.8s ease-out 0.55s both` : "none"}
              >
                {t("landing.manifesto.signature")}
              </Text>
            </Container>
          </Box>
        );
      })()}

      {/* ============================================ */}
      {/* SCROLL SECTIONS — alternating left/right */}
      {/* ============================================ */}
      <Box position="relative" zIndex="1">
        {/* Vertical accent line — timeline feel */}
        <Box
          display={{ base: "none", lg: "block" }}
          position="absolute" top="0" bottom="0" left="50%"
          w="1px"
          bg="linear-gradient(to bottom, transparent, rgba(34, 197, 94, 0.12) 20%, rgba(34, 197, 94, 0.12) 80%, transparent)"
          transform="translateX(-50%)"
        />

        <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
          {scrollSections.map((section, index) => (
            <ScrollSection
              key={index}
              align={section.align}
              labelKey={section.labelKey}
              titleKey={section.titleKey}
              descKey={section.descKey}
              accentColor={section.accentColor}
              image={section.image}
              variant={section.variant}
              delay={0}
            />
          ))}
        </Container>
      </Box>

      {/* ============================================ */}
      {/* PDF REPORTS — official financial statements */}
      {/* ============================================ */}
      {(() => {
        const [pdfRef, pdfInView] = useInView({ threshold: 0.15 });
        return (
          <Box
            ref={pdfRef}
            position="relative"
            zIndex="1"
            py={{ base: 20, md: 32 }}
            overflow="hidden"
          >
            {/* Subtle glow */}
            <Box
              position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
              w="600px" h="600px" borderRadius="full"
              bg="radial-gradient(circle, rgba(239, 68, 68, 0.06) 0%, transparent 60%)"
              filter="blur(100px)" pointerEvents="none"
            />

            <Container maxW="container.md" position="relative">
              {/* Badge */}
              <Flex
                justify="center"
                mb={6}
                opacity={pdfInView ? 1 : 0}
                animation={pdfInView ? `${heroReveal} 0.8s ease-out both` : "none"}
              >
                <Flex
                  w={{ base: "52px", md: "64px" }}
                  h={{ base: "52px", md: "64px" }}
                  borderRadius="xl"
                  bg="rgba(239, 68, 68, 0.10)"
                  border="1px solid rgba(239, 68, 68, 0.20)"
                  align="center"
                  justify="center"
                  mb={2}
                >
                  <Icon as={FaFilePdf} w={{ base: 6, md: 7 }} h={{ base: 6, md: 7 }} color="#ef4444" />
                </Flex>
              </Flex>

              {/* Label */}
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="4px"
                color="#ef4444"
                textAlign="center"
                mb={6}
                opacity={pdfInView ? 1 : 0}
                animation={pdfInView ? `${heroReveal} 0.8s ease-out 0.1s both` : "none"}
              >
                {t("landing.pdf_report.badge")}
              </Text>

              {/* Title */}
              <Heading
                fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                fontWeight="extrabold"
                color="white"
                textAlign="center"
                lineHeight="1.2"
                mb={6}
                letterSpacing="-1px"
                opacity={pdfInView ? 1 : 0}
                animation={pdfInView ? `${heroReveal} 0.8s ease-out 0.2s both` : "none"}
              >
                {t("landing.pdf_report.title")}
              </Heading>

              {/* Description */}
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.400"
                textAlign="center"
                lineHeight="1.8"
                maxW="600px"
                mx="auto"
                opacity={pdfInView ? 1 : 0}
                animation={pdfInView ? `${heroReveal} 0.8s ease-out 0.3s both` : "none"}
              >
                {t("landing.pdf_report.desc")}
              </Text>
            </Container>
          </Box>
        );
      })()}

      {/* ============================================ */}
      {/* FINAL CTA — full-width cinematic */}
      {/* ============================================ */}
      {(() => {
        const [ctaRef, ctaInView] = useInView();
        return (
          <Box
            ref={ctaRef}
            position="relative" zIndex="1"
            py={{ base: 20, md: 32 }}
            textAlign="center"
          >
            {/* Glow behind CTA */}
            <Box
              position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
              w="500px" h="500px" borderRadius="full"
              bg="radial-gradient(circle, rgba(34, 197, 94, 0.12) 0%, transparent 60%)"
              filter="blur(80px)" pointerEvents="none"
            />
            <Container maxW="container.md" position="relative">
              <Heading
                fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                fontWeight="extrabold"
                color="white"
                mb={5}
                letterSpacing="-1px"
                opacity={ctaInView ? 1 : 0}
                animation={ctaInView ? `${heroReveal} 0.8s ease-out both` : "none"}
              >
                {t("landing.cta.title")}
              </Heading>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.500"
                mb={10}
                opacity={ctaInView ? 1 : 0}
                animation={ctaInView ? `${heroReveal} 0.8s ease-out 0.15s both` : "none"}
              >
                {t("landing.cta.subtitle")}
              </Text>
              <Box
                opacity={ctaInView ? 1 : 0}
                animation={ctaInView ? `${heroReveal} 0.8s ease-out 0.3s both` : "none"}
              >
                <MufynButton
                  as={RouterLink}
                  to="/auth/signup"
                  variant="primary"
                  size="lg"
                  rightIcon={<FaArrowRight />}
                  withShimmer
                  withRipple
                >
                  {t("landing.hero.cta_try")}
                </MufynButton>
              </Box>
            </Container>
          </Box>
        );
      })()}

      {/* ============================================ */}
      {/* FOOTER — minimal */}
      {/* ============================================ */}
      <Box py={{ base: 8, md: 10 }} borderTop="1px solid rgba(255, 255, 255, 0.04)" position="relative" zIndex="1">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={4}>
            <HStack spacing={3}>
              <img src={logo} width="28px" alt="Mufyn" />
              <Text color="gray.600" fontSize="sm">
                © {new Date().getFullYear()} {config.appName}
              </Text>
            </HStack>
            <HStack spacing={{ base: 4, md: 6 }}>
              <MufynButton as={RouterLink} to="/auth/faq" variant="link" size="sm">
                FAQ
              </MufynButton>
              <MufynButton as={RouterLink} to="/auth/signup" variant="link" size="sm">
                {t("landing.footer.pricing")}
              </MufynButton>
              <Link href="https://nelx.net/privacy" isExternal color="gray.600" fontSize="sm" _hover={{ color: "#4ade80" }} transition="color 0.2s">
                {t("landing.footer.privacy")}
              </Link>
              <Text color="gray.700" fontSize="xs" fontFamily="monospace">
                v{packageJson.version}
              </Text>
              <Link href="https://nelx.net" isExternal color="gray.600" fontSize="sm" _hover={{ color: "#4ade80" }} transition="color 0.2s">
                NELX
              </Link>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
